import type React from 'react';
import { Globe, BarChart3, ClipboardCheck, MapPin, Store, Building2, ShieldCheck } from 'lucide-react';
import { workflowUrlSlug } from '@dashai/lib/workflowUrlSlugs';
import type { BrowseEntry } from '@dashai/utils/sfaVariants';
import type { CardIconName } from '@/data/dashboardCards';

// Icon names in the dashboard-card catalogue → lucide components. Lives here
// (not in the data module) so the catalogue stays pure data, and every
// navigation shell draws the same icon for the same dashboard.
// Keyed by CardIconName, so adding a name to the union without wiring its
// icon here is a compile error rather than a silent fallback to Globe.
export const DASHBOARD_ICONS: Record<CardIconName, React.ElementType> = {
  Globe,
  BarChart3,
  ClipboardCheck,
  MapPin,
  Store,
  Building2,
  ShieldCheck,
};

// The DashAI destinations every navigation shell links to.
export const DASHAI_ROUTES = {
  newChat: '/ai',
  // The chat page: an empty conversation beside the chat history, which the
  // top bar's DashAI entry and its New chat open.
  chatPage: '/ai/chat',
  autonomousAgent: '/ai/automated-insights',
  scheduledReports: '/ai/automated-reporting',
  savedCanvas: '/ai/saved-canvas',
} as const;

export const chatRoute = (chatId: string) => `/ai/chat/${chatId}`;

// Supplied inside DashAI so a navigation entry takes the same launch path as
// the equivalent home card. Absent on analytics pages, where DashAI is not
// mounted and a route change is the only way in. A per-franchise SFA entry
// passes its pair as the second argument.
export type LaunchWorkflow = (
  agentId: string,
  variantPrefill?: { franchise: string; country: string },
) => void;

// The route a navigation entry opens. A per-franchise SFA entry carries its
// pair plus autorun=1 so DashAI's capture starts the run, a workflow with an
// input dialog opens its detail view, and the rest go through DashAI's ?agent=
// capture.
export function browseEntryHref(entry: BrowseEntry): string {
  if (entry.kind === 'variant') {
    const params = new URLSearchParams({
      agent: entry.base,
      franchise: entry.prefill.franchise,
      country: entry.prefill.country,
      autorun: '1',
    });
    return `/ai?${params.toString()}`;
  }
  const { agent } = entry;
  return agent.isNavigationShortcut || !agent.hasInputDialog
    ? `/ai?agent=${encodeURIComponent(agent.id)}`
    : `/ai/workflow-agents/${workflowUrlSlug(agent.id)}`;
}

// Hands an entry to DashAI's in-page launcher, the one launch path the home
// cards use.
export function launchInPage(entry: BrowseEntry, onLaunchWorkflow: LaunchWorkflow): void {
  if (entry.kind === 'variant') onLaunchWorkflow(entry.base, entry.prefill);
  else onLaunchWorkflow(entry.agent.id);
}

// Starts a workflow from any navigation shell, so an entry cannot behave
// differently depending on whether the sidebar or the top bar was clicked.
// Inside DashAI the in-page launcher takes over; on an analytics page there is
// none, so the entry routes to its href.
export function launchBrowseEntry(
  entry: BrowseEntry,
  onLaunchWorkflow: LaunchWorkflow | undefined,
  navigate: (to: string) => void,
): void {
  if (onLaunchWorkflow) launchInPage(entry, onLaunchWorkflow);
  else navigate(browseEntryHref(entry));
}
